import {
  type Board,
  isBoardMissingOrUnavailable,
} from "@/shared/domain/boards";
import {
  type BoardServiceForOrg,
  type DryRunReport,
} from "@/shared/domain/boards/boardAuthoringService";
import { viewerControlChoicesFor } from "@/shared/domain/boards/viewerControlChoices";

import { withoutServerInjectedFields } from "../toolInput";

import { ApprovalMode } from "./approvalModes";
import {
  boardToolDeps,
  withDryRunContextAndBoardAccess,
} from "./boardAuthoringDeps";
import {
  blockedOnlyByFailuresARerunCanPass,
  CALL_AGAIN_WHEN_A_RERUN_CAN_PASS,
  reportForTheChat,
  WHERE_A_REPORT_KEEPS_ITS_FAILURES,
} from "./boardDryRunResult";
import {
  dryRunBoardInputSchema,
  definitionFromToolInput,
  describeDefinitionShapeProblem,
} from "./boardToolSchema";
import {
  type ClientToolDeps,
  type ClientToolExecutionResult,
  createClientTool,
  toolError,
  ToolErrorKind,
  toolSuccess,
} from "./ClientTools";

/** Where a report that passed keeps its warnings. */
const WARNINGS_NOTE =
  "Warnings do not block; they are in report.warnings (about the board), " +
  "report.stages[].warnings and report.panels[].warnings.";

/** The note beside a report of a new board, by the report's status. */
const NEW_BOARD_NOTES: Record<DryRunReport["status"], string> = {
  ok: `client_create_board will accept this definition. ${WARNINGS_NOTE}`,
  blocked: `client_create_board would refuse this definition. ${WHERE_A_REPORT_KEEPS_ITS_FAILURES} Fix all of them, then create.`,
};

/** The note beside a report of a saved board's new definition, by the report's status. */
const SAVED_BOARD_NOTES: Record<DryRunReport["status"], string> = {
  ok: `client_update_board will accept an update that leaves the board as this definition. ${WARNINGS_NOTE}`,
  blocked: `client_update_board would refuse an update that leaves the board as this definition. ${WHERE_A_REPORT_KEEPS_ITS_FAILURES} Fix all of them, then update.`,
};

export type DryRunBoardInput = Record<string, unknown>;

/**
 * Runs the dry run `client_create_board` runs after approval, offered on its
 * own so the model can check that a definition's stages and panels run before
 * it asks the user to approve anything. With a `board_id`, it runs the dry
 * run `client_update_board` runs on that saved board instead, with the
 * board's stored definition as `storedDefinition`: a `sql` stage is not
 * refused for SQL that built-in stages express when the board has a `sql`
 * stage with the same id, a drawing fault the saved board already has
 * does not block, and when the board is on screen its stages run with the
 * values the viewer chose in its controls.
 *
 * It runs queries the user could run from the boards page, writes nothing, and
 * navigates nowhere, so the tool auto-executes by default like
 * `client_visualize_search_topic`. The report is what the model asked for, so
 * a blocked report is still a successful tool result; the model reads `status`
 * and the failures and fixes the definition, or, when no failure comes from
 * the definition, runs the same dry run again.
 */
async function execute(
  input: DryRunBoardInput,
  deps?: ClientToolDeps,
): Promise<ClientToolExecutionResult> {
  const { board_id: boardId, ...definitionInput } =
    withoutServerInjectedFields(input);
  if (
    boardId !== undefined &&
    (typeof boardId !== "string" || boardId === "")
  ) {
    return toolError(
      "board_id must be the id of a saved board; find ids with client_read_board or in misc_context.board. Leave it out to check a new board.",
      { member: "board_id" },
      ToolErrorKind.VALIDATION,
    );
  }
  const definition = definitionFromToolInput(definitionInput);
  const shapeProblem = describeDefinitionShapeProblem(definition);
  if (shapeProblem !== null) {
    return toolError(
      shapeProblem.message,
      { member: shapeProblem.member },
      ToolErrorKind.VALIDATION,
    );
  }
  return withDryRunContextAndBoardAccess(
    boardToolDeps(deps),
    async (service, ctx, boards) => {
      let storedDefinition: unknown;
      if (boardId !== undefined) {
        const board = await savedBoard(boardId, boards);
        if (board === undefined) {
          return toolError(
            `No board with id "${boardId}" is available to you; client_read_board without board_id lists the boards you can read.`,
            {},
            ToolErrorKind.NOT_FOUND,
          );
        }
        storedDefinition = board.board_definition;
      }
      const report = await service.dryRun(definition, ctx, {
        storedDefinition,
        viewerChoices:
          boardId === undefined ? undefined : viewerControlChoicesFor(boardId),
      });
      const notes = boardId === undefined ? NEW_BOARD_NOTES : SAVED_BOARD_NOTES;
      return toolSuccess("dry_ran_board", {
        status: report.status,
        note: blockedOnlyByFailuresARerunCanPass(report)
          ? CALL_AGAIN_WHEN_A_RERUN_CAN_PASS
          : notes[report.status],
        report: reportForTheChat(report),
      });
    },
  );
}

/** The board `boardId`, or undefined when no board with that id is
 *  available to the caller. Any other error from the read propagates. */
async function savedBoard(
  boardId: string,
  boards: BoardServiceForOrg,
): Promise<Board | undefined> {
  try {
    return await boards.boardService.getBoardById(boardId, boards.orgId);
  } catch (error) {
    if (isBoardMissingOrUnavailable(error)) {
      return undefined;
    }
    throw error;
  }
}

export const tool = createClientTool<DryRunBoardInput>({
  name: "client_dry_run_board",
  description:
    "Dry-runs a board definition against live data without saving, and " +
    "returns the report client_create_board acts on: status ('ok' or " +
    "'blocked'); resolved_time_range, the board time range the stages ran " +
    "over, as start and end ISO 8601 times in the board's time zone with " +
    "its offset; for each stage its row count, columns, up to two sample " +
    "rows (a timestamp column written the same way) and any failure; for " +
    "each panel its status, whether its chart " +
    "compiled and drew marks, which of its views show no tooltip and " +
    "why, which of its legends " +
    "select series when clicked, and any failure; and warnings. 'blocked' " +
    "means client_create_board would refuse this exact definition. With " +
    "board_id, the report is the one client_update_board acts on for an " +
    "update that leaves that saved board as this definition. Topic data " +
    "is not decoded, and only a topic.data stage with a sessions scope " +
    "has its topic and fields checked. " +
    "Read-only. The input is the definition itself, in the shapes " +
    "client_create_board's input schema declares, with board_id beside it " +
    "when it describes a saved board.",
  input_schema: dryRunBoardInputSchema,
  execute,
  defaultApprovalMode: ApprovalMode.AUTO_EXECUTE,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Dry-run board",
  displayDescription:
    "Dry-runs an AI-authored board definition against live data and reports each panel's result to the chat. Creates and changes nothing.",
});
